<template>
  <div>
    <div
      v-if="isLoaded"
      :key="resetKey"
    >
      <b-tabs
        v-model="tabs"
        class="wishlist-tabs"
        nav-wrapper-class="wishlist-list"
      >
        <div
          v-if="canShowSearch"
          class="elements-center wishlist-search-bar pb-50"
        >
          <b-form-input
            v-model="search"
            placeholder="Search"
            class="search-wishlist pl-3"
            @input="searchWishlist"
          />
          <feather-icon
            icon="SearchIcon"
            size="18"
            class="wishlist-search-icon"
          />

          <div
            v-if="(isEntityAdmin || isSalesManager) && isBrand"
            class="ml-1 w-25"
          >
            <reps-list-dropdown
              :options="salesReps"
              :show-self-option="tabs === 0"
              @on-select-options="onFilterBySalesRep"
            />
          </div>

          <div
            v-if="isEntityAdmin && isBrand"
            class="ml-1 w-25"
          >
            <reps-list-dropdown
              :options="salesManagers"
              placeholder="Sales manager"
              :show-self-option="tabs === 0"
              @on-select-options="onFilterBySalesManager"
            />
          </div>

          <b-button
            v-if="isBrand && tabs === 0"
            variant="custom-primary"
            class="ml-1 text-nowrap"
            @click="onClickAddCollection"
          >
            <feather-icon
              icon="PlusIcon"
              size="18"
              class="align-text-bottom"
            />
            <span> Add Wishlist </span>
          </b-button>
        </div>
        <div
          v-if="tabs === 1"
          class="row"
        >
          <div class="col-3">
            <reps-list-dropdown
              placeholder="Collection"
              :options="wishlistCollections"
              @on-select-options="onFilterByCollection"
            />
          </div>
          <div class="col-3 pl-0">
            <reps-list-dropdown
              placeholder="Retailer"
              :options="wishlistRetailers"
              @on-select-options="onFilterByRetailer"
            />
          </div>
        </div>

        <b-tab :title="`My ${getWishlistTitle(true)}`">
          <div
            v-if="wishlists.length"
            class="mt-1"
          >
            <section class="wishlist-cards">
              <div
                v-for="wishlist in wishlists"
                :id="'wishlist_id_'+wishlist._id"
                :key="wishlist._id"
                class="wishlist-card-container"
              >
                <div :class="{'disable-pointer-events': isSelectedMultipleSelections}">
                  <wishlist-card
                    :wishlist="wishlist"
                    @fetchWishlists="fetchWishlists"
                    @open-referral-model="openReferralModelModal"
                  />
                </div>
                <b-form-checkbox
                  v-if="isBrand"
                  class="customCheck"
                  :class="{'d-block': isSelectedMultipleSelections}"
                  :checked-value="true"
                  :unchecked-value="false"
                  :checked="selectedSelections.includes(wishlist._id)"
                  @change="onSelectSelection(wishlist)"
                />
              </div>
            </section>
          </div>
          <template v-else>
            <div v-if="search || isSearching">
              <empty-products-message />
            </div>
            <div
              v-else
              class="pt-md-4 pt-2"
            >
              <span class="empty-icon-bg wishlist">
                <feather-icon
                  icon="SearchIcon"
                  size="22"
                />
              </span>
              <b-card-text class="mt-1">
                You don't have any {{ getWishlistTitle(true) }} yet.
                <br>
                Start adding new products from collections.
              </b-card-text>
              <div class="d-flex">
                <div id="explore_button">
                  <b-button
                    v-if="isRetailer"
                    :to="{ name: 'explore' }"
                    variant="custom-secondary"
                    :disabled="!isEnabledMarketplace"
                  >
                    Go to Collections
                  </b-button>
                </div>
                <b-button
                  v-if="isBrand"
                  variant="custom-secondary"
                  @click="onClickAddCollection"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="18"
                    class="align-text-bottom"
                  />
                  <span> Add Wishlist </span>

                </b-button>
              </div>
              <b-tooltip
                v-if="!isEnabledMarketplace"
                target="explore_button"
                placement="bottom"
              >
                {{ marketplaceAccessDeniedMessage }}
              </b-tooltip>
            </div>
          </template>
        </b-tab>
        <b-tab v-if="isRetailer">
          <template #title>
            <span>SHARED BY BRANDS</span>
          </template>
          <div
            v-if="isRetailer && wishlistsBrand.length"
            class="mt-1"
          >
            <section class="wishlist-cards">
              <wishlist-card
                v-for="wishlist in wishlistsBrand"
                :key="wishlist._id"
                :wishlist="wishlist"
                @fetchWishlists="fetchWishlists"
              />
            </section>
          </div>
          <template v-else>
            <div v-if="search || isSearching">
              <empty-products-message />
            </div>
            <div
              v-else
              class="pt-md-4 pt-2"
            >
              <span class="empty-icon-bg wishlist">
                <message-icon />
              </span>
              <b-card-text class="mt-1">
                No brand has shared the wishlist.
              </b-card-text>
            </div>
          </template>
        </b-tab>
        <b-tab
          v-if="isBrand"
          title="Wishlists with my products"
        >
          <div
            v-if="wishlistsRetailer.length"
            class="mt-1"
          >
            <section class="wishlist-cards">
              <wishlist-card-for-brand
                v-for="wishlist in wishlistsRetailer"
                :key="wishlist._id"
                :wishlist="wishlist"
                @fetchWishlists="fetchWishlists"
                @open-wishlist-notification="openWishlistNotificationModal"
              />
            </section>
          </div>
          <div
            v-else
          >
            <div v-if="search || isSearching">
              <empty-products-message />
            </div>
            <div
              v-else
              class="pt-md-4 pt-2"
            >
              <span class="empty-icon-bg wishlist">
                <message-icon />
              </span>
              <b-card-text class="mt-1">No retailer has added your products to their
                wishlist.</b-card-text>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>
    <b-card
      v-else
      class="elements-center p-3"
    >
      <b-spinner />
    </b-card>
    <referral-model
      :is-selection="true"
      :referral-email="referralEmail"
      :referral-wishlist-id="referralWishlistId"
      :wishlist="selectedWishlist"
    />
    <create-selection-modal
      v-if="isBrand"
      :fetch-wishlists="fetchWishlists"
    />
    <send-wishlist-mail-notification
      v-if="isBrand"
      :retailer-name="retailerName"
      :retailer-email="retailerEmail"
    />
    <tool-kit :toolkit-page="TOOLKIT_PAGE.WISHLIST" />
    <share-selections-error-modal
      :modal-id="shareSelectionsErrorModalId"
      :messages="shareSelectionResponseMessages"
    />
    <wishlist-action
      v-if="isSelectedMultipleSelections"
      :is-multiple-selections="isSelectedMultipleSelections"
      @fetchWishlists="fetchWishlists"
      @on-open-errors-popup="showShareSelectionsErrorModal"
    />
  </div>
</template>

<script>
import MessageIcon from '@/@core/assets/svg-components/MessageIcon.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'

import {
BButton,
BCard,
BCardText,
BFormCheckbox,
BFormInput,
BSpinner,
BTab,
BTabs,
BTooltip,
VBModal,
} from 'bootstrap-vue'
import { useWishlistList } from '@/composables/useWishlistList'
import UserRoleMixin from '../UserRoleMixin.vue'
import ReferralModel from '../referral/referralModel.vue'
import EmptyProductsMessage from '../shop/EmptyProductsMessage.vue'
import CreateSelectionModal from './CreateSelectionModal.vue'
import SendWishlistMailNotification from './SendWishlistMailNotification.vue'
import WishlistCard from './WishlistCard.vue'
import WishlistCardForBrand from './WishlistCardForBrand.vue'
import RepsListDropdown from '../wallet/RepsListDropdown.vue'
import WishlistAction from './WishlistAction.vue'
import ShareSelectionsErrorModal from './ShareSelectionsErrorModal.vue'

export default {
  components: {
    BCard,
    FeatherIcon,
    BFormInput,
    WishlistCard,
    BSpinner,
    BCardText,
    BButton,
    EmptyProductsMessage,
    ReferralModel,
    CreateSelectionModal,
    BTabs,
    BTab,
    BTooltip,
    MessageIcon,
    WishlistCardForBrand,
    SendWishlistMailNotification,
    RepsListDropdown,
    BFormCheckbox,
    WishlistAction,
    ShareSelectionsErrorModal,
  },
  directives: {
    'b-modal': VBModal,
  },
  mixins: [UserRoleMixin],
  setup() {
    const wl = useWishlistList()
    return {
      ...wl,
    }
  },
  mounted() {
    this.$root.$refs.selection = this
    this.initWishlistList()
  },
  destroyed() {
    this.destroyWishlistList()
  },
  methods: {
    openReferralModelModal(email, wishlist) {
      this.openReferralModel(email, wishlist)
      this.$bvModal.show('referal-model')
    },
    onClickAddCollection() {
      this.$bvModal.show('create_selection_modal')
      analytics.track(constants.TRACKS.ACTIONS.BRAND_CLICKS_ADD_SELECTION, {
        'Created By': new Date(),
      })
    },
    openWishlistNotificationModal(name, email) {
      this.openWishlistNotification(name, email)
      this.$bvModal.show('send-wishlist-mail-notification')
    },
    showShareSelectionsErrorModal(messages) {
      this.openShareSelectionsErrorModal(messages)
      this.$bvModal.show(this.shareSelectionsErrorModalId)
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-wishlist.scss';
@import '~@core/scss/base/pages/app-ecommerce.scss';
@import '~@core/scss/base/pages/app-shop.scss';

.wishlist-card-container
{
  position: relative;

.disable-pointer-events {
    pointer-events: none;

    .share-icon-position {
      rect {
        fill: $gray-100;
      }
    }
  }
  .customCheck {
    position: absolute;
    top: 0;
    display: none;
    margin: 10px;
    z-index: 0 !important;
    user-select: none
  }
  &:hover {
    .wishlist-card-details {
      border: 1px solid rgba($black, 0.363);
      border-radius: 6px;
    }
    .customCheck {
      display: initial;
    }
  }
}
</style>
